/**
 * Decoder performance test — pure cap-level orchestrator.
 *
 * Drives the registered `decoder` cap via tRPC primitives (createSession /
 * pushPacket / destroySession / getStats / listActiveSessions) and samples
 * the hub process's memory + cpu at a fixed cadence. Emits progress on the
 * `benchmark.progress` event category with `kind: 'decoder-perf'` + the
 * run's `sessionId` so a single client can filter its own run.
 *
 * No direct class imports, no worker forks: any addon that registers the
 * decoder cap is testable without modification.
 */
import * as fs from 'node:fs'
import * as path from 'node:path'
import { performance } from 'node:perf_hooks'
import { cpuUsage, memoryUsage } from 'node:process'
import type { AddonApi, IEventBus, IScopedLogger } from '@camstack/types'
import { createEvent, nodePin } from '@camstack/types'
import type { z } from 'zod'
import type {
  DecoderPerfInputSchema,
  DecoderPerfProgressSchema,
  DecoderPerfResultSchema,
  DecoderPerfSampleSchema,
} from './benchmark-schemas.js'

type DecoderPerfInput = z.infer<typeof DecoderPerfInputSchema>
type DecoderPerfResult = z.infer<typeof DecoderPerfResultSchema>
type DecoderPerfSample = z.infer<typeof DecoderPerfSampleSchema>
type DecoderPerfProgress = z.infer<typeof DecoderPerfProgressSchema>

export interface DecoderPerfRunDeps {
  readonly api: AddonApi
  readonly eventBus: IEventBus | undefined
  readonly logger: IScopedLogger
  readonly fixturesDir: string
  readonly abortFlag: { aborted: boolean }
}

interface SessionHandle {
  readonly sessionId: string
  /** nodeId returned by createSession — pins every subsequent call
   *  (pushPacket / getStats / destroy) to the owning provider. */
  readonly nodeId: string
  readonly codec: string
  offsetBytes: number
  framesPushed: number
}

const CHUNK_SIZE = 4096

function emitProgress(eventBus: IEventBus | undefined, payload: DecoderPerfProgress): void {
  if (!eventBus) return
  eventBus.emit(
    createEvent(
      'benchmark.progress',
      { type: 'benchmark', id: 'decoder-perf' },
      {
        kind: payload.kind,
        sessionId: payload.sessionId,
        phase: payload.phase,
        tSec: payload.tSec,
        sample: payload.sample ? { ...payload.sample } : undefined,
        message: payload.message,
      },
    ),
  )
}

/** Ordinary-least-squares slope of y vs x. Returns 0 when fewer than 2 samples. */
function slope(xs: readonly number[], ys: readonly number[]): number {
  if (xs.length < 2) return 0
  const n = xs.length
  let sx = 0,
    sy = 0,
    sxx = 0,
    sxy = 0
  for (let i = 0; i < n; i++) {
    sx += xs[i]!
    sy += ys[i]!
    sxx += xs[i]! * xs[i]!
    sxy += xs[i]! * ys[i]!
  }
  const denom = n * sxx - sx * sx
  return denom === 0 ? 0 : (n * sxy - sx * sy) / denom
}

function verdictFor(
  rssSlopeMBMin: number,
  warnMBMin: number,
  failMBMin: number,
): DecoderPerfResult['verdict'] {
  if (rssSlopeMBMin >= failMBMin) return 'fail'
  if (rssSlopeMBMin >= warnMBMin) return 'warn'
  return 'pass'
}

function resolveFixtureCodec(filename: string): 'h264' | 'h265' {
  if (filename.endsWith('.hevc') || filename.endsWith('.h265')) return 'h265'
  return 'h264'
}

function readFixtureBytes(fixturesDir: string, filename: string): Buffer {
  const full = path.join(fixturesDir, filename)
  if (!fs.existsSync(full)) {
    throw new Error(`decoder fixture not found: ${filename}`)
  }
  return fs.readFileSync(full)
}

async function createSessions(
  api: AddonApi,
  input: DecoderPerfInput,
  codec: string,
): Promise<SessionHandle[]> {
  const handles: SessionHandle[] = []
  // Pin createSession to the selected node so the sessions are created on the
  // chosen agent (not the hub). The returned nodeId then routes every
  // subsequent pushPacket/getStats/destroySession call back to that node.
  const pin = input.nodeId ? nodePin(input.nodeId) : undefined
  for (let i = 0; i < input.cameras; i++) {
    const created = await api.decoder.createSession.query(
      {
        codec,
        maxFps: input.fps,
        outputFormat: input.format === 'gray' ? 'gray' : 'jpeg',
        scale: 1,
      },
      pin,
    )
    handles.push({
      sessionId: created.sessionId,
      nodeId: created.nodeId,
      codec,
      offsetBytes: 0,
      framesPushed: 0,
    })
  }
  return handles
}

async function destroySessions(
  api: AddonApi,
  handles: readonly SessionHandle[],
  logger: IScopedLogger,
): Promise<void> {
  for (const h of handles) {
    try {
      await api.decoder.destroySession.query({ sessionId: h.sessionId, nodeId: h.nodeId })
    } catch (err: unknown) {
      logger.warn('destroySession failed', {
        meta: { sessionId: h.sessionId, error: err instanceof Error ? err.message : String(err) },
      })
    }
  }
}

async function pushChunk(api: AddonApi, handle: SessionHandle, fixture: Buffer): Promise<void> {
  if (handle.offsetBytes >= fixture.length) handle.offsetBytes = 0
  const end = Math.min(handle.offsetBytes + CHUNK_SIZE, fixture.length)
  const chunk = fixture.subarray(handle.offsetBytes, end)
  handle.offsetBytes = end
  const now = Date.now()
  await api.decoder.pushPacket.query({
    sessionId: handle.sessionId,
    nodeId: handle.nodeId,
    packet: {
      type: 'video',
      data: new Uint8Array(chunk),
      pts: now,
      dts: now,
      keyframe: handle.offsetBytes <= CHUNK_SIZE,
      codec: handle.codec,
    },
  })
  handle.framesPushed += 1
}

async function sumDroppedFrames(api: AddonApi, handles: readonly SessionHandle[]): Promise<number> {
  let dropped = 0
  for (const h of handles) {
    try {
      const stats = await api.decoder.getStats.query({ sessionId: h.sessionId, nodeId: h.nodeId })
      dropped += stats.droppedFrames
    } catch {
      // session may have been destroyed; ignore
    }
  }
  return dropped
}

export async function runDecoderPerfTest(
  input: DecoderPerfInput,
  deps: DecoderPerfRunDeps,
): Promise<DecoderPerfResult> {
  const { api, eventBus, logger, fixturesDir, abortFlag } = deps
  const sessionId = input.sessionId
  const samples: DecoderPerfSample[] = []
  const startWall = performance.now()

  emitProgress(eventBus, {
    kind: 'decoder-perf',
    sessionId,
    phase: 'init',
    message: 'initialising',
  })

  if (input.source.kind !== 'sample') {
    const msg = 'decoder perf: stream source not yet implemented — use a sample fixture'
    emitProgress(eventBus, { kind: 'decoder-perf', sessionId, phase: 'error', message: msg })
    return {
      sessionId,
      verdict: 'error',
      aborted: false,
      errorMessage: msg,
      samples: [],
      slopes: { rssMBMin: 0, heapMBMin: 0, externalMBMin: 0 },
      totals: { wallSec: 0, framesEmitted: 0, fpsPerCamera: 0, cpuAvgPct: 0, droppedFrames: 0 },
      config: input,
    }
  }

  const fixtureName = input.source.fixtureFilename
  const codec = resolveFixtureCodec(fixtureName)
  const fixture = readFixtureBytes(fixturesDir, fixtureName)

  // Observability: record concurrent decoder sessions (warn only). When
  // decoder runs in an isolated worker the per-PID stats from
  // metricsProvider give cleaner measurements — warn if in-process.
  try {
    const active = await api.decoder.listActiveSessions.query()
    if (active.length > 0) {
      logger.warn('concurrent decoder sessions detected — measurement may be contaminated', {
        meta: { concurrent: active.length },
      })
    }
  } catch {
    // listActiveSessions absent on some providers; ignore.
  }
  try {
    const instances = await api.metricsProvider.listAddonInstances.query()
    const workerInstances = instances.filter((i) => i.role === 'worker')
    if (workerInstances.length === 0) {
      logger.info(
        'decoder perf: no isolated worker detected — hub-local measurement (process.memoryUsage) mixes decoder + hub workload',
      )
    } else {
      logger.info('decoder perf: worker instances visible', {
        meta: { workers: workerInstances.map((w) => w.addonId) },
      })
    }
  } catch {
    // metricsProvider cap may be absent on degraded setups.
  }

  let handles: SessionHandle[]
  try {
    handles = await createSessions(api, input, codec)
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err)
    emitProgress(eventBus, {
      kind: 'decoder-perf',
      sessionId,
      phase: 'error',
      message: `createSession failed: ${msg}`,
    })
    return {
      sessionId,
      verdict: 'error',
      aborted: false,
      errorMessage: msg,
      samples: [],
      slopes: { rssMBMin: 0, heapMBMin: 0, externalMBMin: 0 },
      totals: { wallSec: 0, framesEmitted: 0, fpsPerCamera: 0, cpuAvgPct: 0, droppedFrames: 0 },
      config: input,
    }
  }

  const frameIntervalMs = 1000 / input.fps
  const warmupMs = input.warmupSec * 1000
  const durationMs =
    input.mode.kind === 'duration' ? input.mode.durationSec * 1000 : Number.MAX_SAFE_INTEGER
  const targetIterations =
    input.mode.kind === 'iterations' ? input.mode.iterations : Number.MAX_SAFE_INTEGER

  let cpuSnapshot = cpuUsage()
  let cpuWallMs = performance.now()

  const feedTickerStart = performance.now()
  let nextFeedAt = feedTickerStart
  let nextSampleAt = feedTickerStart + warmupMs
  let aborted = false

  emitProgress(eventBus, {
    kind: 'decoder-perf',
    sessionId,
    phase: 'warmup',
    tSec: 0,
    message: `warming up ${input.warmupSec}s`,
  })

  const loopUntil = feedTickerStart + warmupMs + durationMs
  while (performance.now() < loopUntil) {
    if (abortFlag.aborted) {
      aborted = true
      break
    }
    const totalPushed = handles.reduce((s, h) => s + h.framesPushed, 0)
    if (totalPushed >= targetIterations * handles.length) break

    const now = performance.now()
    if (now >= nextFeedAt) {
      for (const h of handles) {
        try {
          await pushChunk(api, h, fixture)
        } catch (err: unknown) {
          logger.warn('pushPacket failed', {
            meta: {
              sessionId: h.sessionId,
              error: err instanceof Error ? err.message : String(err),
            },
          })
        }
      }
      nextFeedAt += frameIntervalMs
    }

    if (now >= nextSampleAt) {
      const tSec = (now - feedTickerStart) / 1000
      const mem = memoryUsage()
      const cpu = cpuUsage(cpuSnapshot)
      const windowMs = now - cpuWallMs
      const cpuPct = windowMs > 0 ? ((cpu.user + cpu.system) / 1000 / windowMs) * 100 : 0
      cpuSnapshot = cpuUsage()
      cpuWallMs = now
      const sample: DecoderPerfSample = {
        tSec,
        rssMB: mem.rss / 1048576,
        heapMB: mem.heapUsed / 1048576,
        externalMB: mem.external / 1048576,
        arrayBuffersMB: mem.arrayBuffers / 1048576,
        cpuPct,
        framesTotal: handles.reduce((s, h) => s + h.framesPushed, 0),
      }
      samples.push(sample)
      emitProgress(eventBus, {
        kind: 'decoder-perf',
        sessionId,
        phase: tSec < 0 ? 'warmup' : 'running',
        tSec,
        sample,
      })
      nextSampleAt += input.sampleIntervalMs
    }

    const sleepMs = Math.max(0, Math.min(nextFeedAt, nextSampleAt) - performance.now())
    if (sleepMs > 0) await new Promise((r) => setTimeout(r, sleepMs))
  }

  const droppedFrames = await sumDroppedFrames(api, handles)
  await destroySessions(api, handles, logger)

  const postWarmup = samples.filter((s) => s.tSec * 1000 >= warmupMs)
  const xs = postWarmup.map((s) => s.tSec)
  const rssYs = postWarmup.map((s) => s.rssMB)
  const heapYs = postWarmup.map((s) => s.heapMB)
  const extYs = postWarmup.map((s) => s.externalMB)

  const rssSlopeMBMin = slope(xs, rssYs) * 60
  const heapSlopeMBMin = slope(xs, heapYs) * 60
  const externalSlopeMBMin = slope(xs, extYs) * 60

  const wallSec = (performance.now() - startWall) / 1000
  const framesEmitted = handles.reduce((s, h) => s + h.framesPushed, 0)
  const fpsPerCamera = wallSec > 0 ? framesEmitted / Math.max(1, handles.length) / wallSec : 0
  const cpuAvgPct =
    samples.length > 0 ? samples.reduce((s, x) => s + x.cpuPct, 0) / samples.length : 0

  const verdict: DecoderPerfResult['verdict'] = aborted
    ? 'aborted'
    : verdictFor(rssSlopeMBMin, input.thresholds.warnMBMin, input.thresholds.failMBMin)

  emitProgress(eventBus, {
    kind: 'decoder-perf',
    sessionId,
    phase: aborted ? 'aborted' : 'complete',
    message: `done — rss slope ${rssSlopeMBMin.toFixed(2)} MB/min`,
  })

  return {
    sessionId,
    verdict,
    aborted,
    samples,
    slopes: {
      rssMBMin: rssSlopeMBMin,
      heapMBMin: heapSlopeMBMin,
      externalMBMin: externalSlopeMBMin,
    },
    totals: { wallSec, framesEmitted, fpsPerCamera, cpuAvgPct, droppedFrames },
    config: input,
  }
}
